import { getByPathOrDefault } from "../../../services/common/object-util";

class BoxScoreCalculator {
    getPassingDowns(teamStats, down, property) {
        return getByPathOrDefault(
            teamStats,
            `stats.offense.downs.pass.${down}.${property}`,
            0
        );
    }

    getRushingDowns(teamStats, down, property) {
        return getByPathOrDefault(
            teamStats,
            `stats.offense.downs.rush.${down}.${property}`,
            0
        );
    }

    getPenaltyDowns(teamStats, down, property) {
        return getByPathOrDefault(
            teamStats,
            `stats.offense.downs.penalty.${down}.${property}`,
            0
        );
    }

    /**
     * Retrieve the combined down stats for a given amount of downs and a team stat collection
     * @param {FootballTeamStatsCollection} teamStats
     * @param {number} downs
     * @returns {{ passing: number, rushing: number, penalty: number, total: number, attempted: number, touchdowns: number}}
     */
    getCombinedDownStats(teamStats, downs) {
        const downArr = Array.from({ length: downs }, (_, index) => index).map(
            (i) => i + 1
        );

        return downArr.reduce(
            (acc, down) => {
                const result = this.getDownStatsByTeam(teamStats, down);
                acc.passing = acc.passing + result.passing;
                acc.rushing = acc.rushing + result.rushing;
                acc.penalty = acc.penalty + result.penalty;
                acc.total = acc.total + result.total;
                acc.attempted = acc.attempted + result.attempted;
                acc.touchdowns = acc.touchdowns + result.touchdowns;
                return acc;
            },
            {
                passing: 0,
                rushing: 0,
                penalty: 0,
                total: 0,
                attempted: 0,
                touchdowns: 0
            }
        );
    }

    getDownStatsByTeam(teamStats, down, includePenalty = true) {
        const passing = this.getPassingDowns(teamStats, down, "completed");
        const rushing = this.getRushingDowns(teamStats, down, "completed");
        const penalty = this.getPenaltyDowns(teamStats, down, "completed");

        const passingAttempted = this.getPassingDowns(
            teamStats,
            down,
            "attempted"
        );
        const rushingAttempted = this.getRushingDowns(
            teamStats,
            down,
            "attempted"
        );
        const penaltyAttempted = includePenalty
            ? this.getPenaltyDowns(teamStats, down, "attempted")
            : 0;

        const passingTouchdowns = this.getPassingDowns(
            teamStats,
            down,
            "touchdowns"
        );
        const rushingTouchdowns = this.getRushingDowns(
            teamStats,
            down,
            "touchdowns"
        );
        const penaltyTouchdowns = includePenalty
            ? this.getPenaltyDowns(teamStats, down, "touchdowns")
            : 0;

        return {
            passing,
            rushing,
            penalty,
            total: passing + rushing + penalty,
            attempted: passingAttempted + rushingAttempted + penaltyAttempted,
            touchdowns:
                passingTouchdowns + rushingTouchdowns + penaltyTouchdowns
        };
    }

    getFirstDownStatsByTeam(teamStats) {
        return this.getDownStatsByTeam(teamStats, 1);
    }

    getThirdDownStatsByTeam(teamStats) {
        return this.getDownStatsByTeam(teamStats, 3, false);
    }

    getFourthDownStatsByTeam(teamStats) {
        return this.getDownStatsByTeam(teamStats, 4, false);
    }

    getPassSacked(teamStats) {
        const { completed, totalYards } = getByPathOrDefault(
            teamStats,
            "stats.offense.sack"
        ) || { completed: 0, totalYards: 0 };

        return {
            completed,
            totalYards: totalYards * -1
        };
    }
}

export default new BoxScoreCalculator();
